import { useQuery, type ApolloError, type OperationVariables, type TypedDocumentNode } from '@apollo/client';
import { useLayoutEffect, useState } from 'react';

import { analyticsSectionError, invalidatesAnalyticsScope } from './analytics-access';
import type { NovaAnalyticsDashboardData } from '../../../../queries/nova-analytics';

export const analyticsQueryContext = {
  notifications: { preventErrorNotification: true },
  // An old viewer/request generation must never donate an in-flight result to a new session.
  queryDeduplication: false,
};

export interface AnalyticsInitialResult {
  data?: NovaAnalyticsDashboardData;
  loading: boolean;
  error?: ApolloError;
}

export interface AnalyticsSectionProps {
  initial: AnalyticsInitialResult;
  onInvalidScope: (error: ApolloError) => void;
  onSourceDateChange: (date: string) => void;
}

/** Seed each section from the compound response, then fetch only on interaction/retry. */
export function useAnalyticsSection<Data extends Record<string, unknown>, Variables extends OperationVariables>(
  query: TypedDocumentNode<Data, Variables>,
  variables: Variables,
  field: keyof Data & keyof NovaAnalyticsDashboardData,
  interacted: boolean,
  { initial, onInvalidScope, onSourceDateChange }: AnalyticsSectionProps,
) {
  const [retried, setRetried] = useState(false);
  const live = interacted || retried;
  const request = useQuery(query, {
    variables,
    skip: !live || initial.loading,
    fetchPolicy: 'no-cache',
    nextFetchPolicy: 'no-cache',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
    context: analyticsQueryContext,
  });
  const loading = initial.loading || (live && request.loading);
  const error = analyticsSectionError(live ? request.error : initial.error, field);
  const page = loading || error ? undefined : live ? request.data?.[field] : initial.data?.[field];
  const date = page && typeof page === 'object' && 'asOfDate' in page ? page.asOfDate : undefined;
  useLayoutEffect(() => {
    if (invalidatesAnalyticsScope(error) && error) onInvalidScope(error);
  }, [error, onInvalidScope]);
  useLayoutEffect(() => {
    if (typeof date === 'string') onSourceDateChange(date);
  }, [date, onSourceDateChange]);
  return {
    page: page as Data[typeof field] | undefined,
    loading,
    error,
    retry: () => {
      if (!live) setRetried(true);
      else void request.refetch().catch(() => undefined);
    },
  };
}
